// src/pages/Loads/ManifestDetail.tsx
/**
 * Timber Management System — Loads › Manifest Detail
 *
 * Features
 * • Header summary: container, status pill, seal, voyage/vessel, port, ETA/ETD, fumigation, notes.
 * • Status actions (Admin/Dispatcher): Draft → Ready → Shipped → Delivered → Closed.
 * • Edit drawer for manifest properties; persists to localStorage.
 * • Lines table: auto-resolves from loads:data:v1 (if present) and posted inbox (loads:inbox:v1) by container.
 * • Totals row for Gross/Tare/Net; KPI chips; CSV export of lines; JSON export of manifest+lines; Print.
 * • “Add Samples” populates demo lines for this manifest (helpful when wiring).
 * • Audit + notifications wired.
 *
 * LocalStorage Keys
 * • loads:manifests:v1 (array of manifests) — shared with ManifestsIndex
 * • loads:data:v1     (optional loads data; array of load rows)
 * • loads:inbox:v1    (optional inbox; posted rows may appear here)
 *
 * Routing
 * • Route: /loads/manifest/:container — we locate manifest by container match (case-sensitive).
 */

import React, { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import AppShell from '@/components/AppShell'
import DataTable, { Col } from '@/components/DataTable'
import Drawer from '@/components/Drawer'
import Skeleton from '@/components/Skeleton'
import EmptyState from '@/components/EmptyState'
import { useNotifications } from '@/lib/notifications'
import { get, set } from '@/lib/storage'
import { logAudit } from '@/lib/audit'
import {
  ArrowLeft, Printer, FileJson, Download, Edit, Save,
  Ship, PackageCheck, CircleCheckBig, CheckCheck, Trash2, Info as InfoIcon
} from 'lucide-react'

type Role = 'Admin'|'Dispatcher'|'Loader'|'Trucker'|'Accountant'
const actorRole: Role = get<Role>('role','Admin' as Role)

type Status = 'Draft'|'Ready'|'Shipped'|'Delivered'|'Closed'
type Manifest = {
  id: string
  container: string
  seal?: string
  voyage?: string
  vessel?: string
  port?: string
  siteId?: string
  siteName?: string
  mill?: string
  gross?: number
  tare?: number
  net?: number
  fumigation?: boolean
  eta?: string // ISO date
  etd?: string // ISO date
  status: Status
  notes?: string
  createdAt: string // ISO
  updatedAt?: string
}

type LoadLine = {
  id: string
  ticket?: string
  date?: string // ISO
  siteName?: string
  product?: string
  truck?: string
  gross?: number
  tare?: number
  net?: number
  container?: string
  seal?: string
}

const MANIFESTS_KEY = 'loads:manifests:v1'
const LOADS_KEY     = 'loads:data:v1'
const INBOX_KEY     = 'loads:inbox:v1'
const TBL_LINES_KEY = 'tbl:manifest-lines:v1'

const fmtNum = (n?: number) => Number(n || 0).toLocaleString()
const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : '—')
const cx = (...xs: Array<string | false | null | undefined>) => xs.filter(Boolean).join(' ')
function uid() { return (globalThis.crypto && 'randomUUID' in globalThis.crypto) ? (crypto as any).randomUUID() : Math.random().toString(36).slice(2) }

export default function ManifestDetail() {
  const { container: containerParam } = useParams<{container: string}>()
  const container = decodeURIComponent(containerParam || '')
  const nav = useNavigate()
  const { push } = useNotifications()

  // Load manifest list and locate current
  const [manifests, setManifests] = useState<Manifest[]>(() => normalize(get<Manifest[]>(MANIFESTS_KEY, [])))
  const [manifest, setManifest] = useState<Manifest | null>(() => manifests.find(m => m.container === container) || null)
  const [loading, setLoading] = useState(false)

  // Edit drawer
  const [editing, setEditing] = useState<Manifest | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Derived: can manage?
  const canManage = actorRole === 'Admin' || actorRole === 'Dispatcher'

  useEffect(() => {
    setLoading(true)
    const list = normalize(get<Manifest[]>(MANIFESTS_KEY, []))
    setManifests(list)
    setManifest(list.find(m => m.container === container) || null)
    setLoading(false)
  }, [container])

  // Lines: resolve from loads:data and posted inbox rows
  const lines = useMemo<LoadLine[]>(() => {
    const fromLoads = (get<any[]>(LOADS_KEY, []) || [])
      .filter(x => (x?.container || '') === container)
      .map(toLine)

    const fromInboxPosted = (get<any[]>(INBOX_KEY, []) || [])
      .filter(x => (x?.container || '') === container && x?.status === 'posted')
      .map(toLine)

    // de-dup by ticket+date
    const key = (l: LoadLine) => `${l.ticket || ''}|${l.date || ''}|${l.gross || ''}|${l.net || ''}`
    const map = new Map<string, LoadLine>()
    for (const l of [...fromLoads, ...fromInboxPosted]) map.set(key(l), l)
    return Array.from(map.values())
  }, [container])

  const totals = useMemo(() => {
    return lines.reduce((acc, l) => {
      acc.gross += Number(l.gross || 0)
      acc.tare  += Number(l.tare || 0)
      acc.net   += Number(l.net ?? (Number(l.gross || 0) - Number(l.tare || 0)))
      return acc
    }, { gross: 0, tare: 0, net: 0 })
  }, [lines])

  function persistManifest(next: Manifest) {
    const updated = upsertById(manifests, next)
    set(MANIFESTS_KEY, updated); setManifests(updated); setManifest(next)
  }

  function saveEdit() {
    if (!editing) return
    const item = {
      ...editing,
      net: Number(editing.net ?? (Number(editing.gross||0)-Number(editing.tare||0))),
      updatedAt: new Date().toISOString(),
    }
    persistManifest(item)
    setDrawerOpen(false)
    push({ title: 'Manifest saved', body: item.container })
    logAudit({ action: 'manifest:save', id: item.id, container: item.container }, actorRole)
  }

  function setStatus(next: Status) {
    if (!manifest) return
    const item = { ...manifest, status: next, updatedAt: new Date().toISOString() }
    persistManifest(item)
    push({ title: `Status: ${next}`, body: manifest.container })
    logAudit({ action: 'manifest:status', id: manifest.id, to: next }, actorRole)
  }

  function exportLinesCSV() {
    const header = ['Ticket','Date','Site','Product','Truck','Gross (lbs)','Tare (lbs)','Net (lbs)']
    const rows = lines.map(l => [
      l.ticket || '',
      l.date ? new Date(l.date).toLocaleString() : '',
      l.siteName || '',
      l.product || '',
      l.truck || '',
      String(l.gross || 0),
      String(l.tare || 0),
      String(Number(l.net ?? (Number(l.gross||0)-Number(l.tare||0)))),
    ])
    const blob = new Blob([toCSV([header, ...rows])], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = `manifest_${sanitize(manifest?.container || 'container')}.csv`
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
    push({ title: 'Exported CSV', body: `${rows.length} line(s)` })
    logAudit({ action: 'manifest:export:csv', id: manifest?.id }, actorRole)
  }

  function exportJSON() {
    const payload = { manifest, lines, totals }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = `manifest_${sanitize(manifest?.container || 'container')}.json`
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
    push({ title: 'Exported JSON' })
    logAudit({ action: 'manifest:export:json', id: manifest?.id }, actorRole)
  }

  function addSampleLines() {
    if (!manifest) return
    const existing = get<any[]>(LOADS_KEY, [])
    const now = Date.now()
    const make = (i: number): any => ({
      id: uid(),
      ticket: `T-${1000+i}`,
      date: new Date(now - i*3600_000).toISOString(),
      siteName: i % 2 ? (manifest.siteName || 'Pine Flats') : 'North Ridge',
      product: i % 2 ? 'Pine Pulp' : 'Chip-n-Saw',
      truck: `TRK-${200+i}`,
      gross: 84_000 - i*300,
      tare:  32_000,
      net: undefined,
      container: manifest.container,
      seal: manifest.seal || '',
    })
    const next = [make(1), make(2), make(3), make(4), make(5), make(6)]
    set(LOADS_KEY, [...next, ...existing])
    push({ title: 'Added sample lines', body: `${next.length} lines` })
    logAudit({ action: 'manifest:samples:add', container: manifest.container, count: next.length }, actorRole)
  }

  if (loading) return <AppShell title="Manifest"><Skeleton className="h-64" /></AppShell>

  if (!manifest) {
    return (
      <AppShell title={`Manifest • ${container}`}>
        <EmptyState title="Manifest not found">
          We couldn't find a manifest with container <span className="font-mono">{container}</span>.
          <div className="mt-3 flex items-center gap-2">
            <Link className="btn-secondary" to="/loads/manifests"><ArrowLeft className="w-4 h-4" /> Back to Manifests</Link>
          </div>
        </EmptyState>
      </AppShell>
    )
  }

  // Table columns for lines
  const cols: Col[] = [
    { key: 'ticket',  label: 'Ticket' },
    { key: 'date',    label: 'Date/Time' },
    { key: 'site',    label: 'Site' },
    { key: 'product', label: 'Product' },
    { key: 'truck',   label: 'Truck' },
    { key: 'gross',   label: 'Gross (lbs)' },
    { key: 'tare',    label: 'Tare (lbs)' },
    { key: 'net',     label: 'Net (lbs)' },
  ]
  const rows = lines.map(l => ({
    ticket:  <div className="pl-3 pr-2 min-w-[10rem]">{l.ticket || '—'}</div>,
    date:    <div className="min-w-[12rem] tabular-nums">{l.date ? new Date(l.date).toLocaleString() : '—'}</div>,
    site:    <div className="min-w-[12rem] truncate" title={l.siteName || ''}>{l.siteName || '—'}</div>,
    product: <div className="min-w-[10rem] truncate" title={l.product || ''}>{l.product || '—'}</div>,
    truck:   <div className="min-w-[8rem] truncate" title={l.truck || ''}>{l.truck || '—'}</div>,
    gross:   <div className="min-w-[8rem] text-right tabular-nums">{fmtNum(l.gross)}</div>,
    tare:    <div className="min-w-[8rem] text-right tabular-nums">{fmtNum(l.tare)}</div>,
    net:     <div className="min-w-[8rem] text-right tabular-nums">{fmtNum(l.net ?? (Number(l.gross||0) - Number(l.tare||0)))}</div>,
  }))

  const statusBadge = (s: Status) => {
    const cls =
      s === 'Delivered' ? 'bg-emerald-100 text-emerald-800' :
      s === 'Shipped'   ? 'bg-blue-100 text-blue-800' :
      s === 'Ready'     ? 'bg-amber-100 text-amber-800' :
      s === 'Closed'    ? 'bg-slate-300 text-slate-800' : 'bg-slate-100 text-slate-700'
    return <span className={cx('badge', cls)}>{s}</span>
  }

  return (
    <AppShell title={`Manifest • ${manifest.container}`}>
      {/* Back / actions bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Link to="/loads/manifests" className="btn-secondary">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="text-sm text-slate-600">
            Created {fmtDate(manifest.createdAt)}{manifest.updatedAt ? ` • Updated ${fmtDate(manifest.updatedAt)}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary" onClick={exportLinesCSV} title="Export lines as CSV">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button className="btn-secondary" onClick={exportJSON} title="Export manifest + lines as JSON">
            <FileJson className="w-4 h-4" /> JSON
          </button>
          <button className="btn-secondary" onClick={() => window.print()} title="Print">
            <Printer className="w-4 h-4" /> Print
          </button>
          <button className="btn-secondary" onClick={() => { setEditing({ ...manifest }); setDrawerOpen(true) }} title="Edit">
            <Edit className="w-4 h-4" /> Edit
          </button>
        </div>
      </div>

      {/* Header Summary */}
      <div className="card mb-3">
        <div className="card-body grid lg:grid-cols-3 gap-3 items-start">
          <div className="lg:col-span-1">
            <div className="text-xs muted mb-1">Container</div>
            <div className="text-xl font-semibold">{manifest.container} <span className="ml-2">{statusBadge(manifest.status)}</span></div>
            <div className="mt-2 flex flex-wrap gap-2">
              {manifest.seal && <span className="badge">Seal {manifest.seal}</span>}
              {manifest.fumigation && <span className="badge">Fumigation</span>}
              {manifest.port && <span className="badge">{manifest.port}</span>}
              {manifest.siteName && <span className="badge">{manifest.siteName}</span>}
              {manifest.mill && <span className="badge">{manifest.mill}</span>}
            </div>
            {manifest.notes && <div className="text-sm mt-2">{manifest.notes}</div>}

            {/* Status Actions */}
            <div className="mt-3 flex flex-wrap gap-2">
              {canManage && manifest.status !== 'Closed' && (
                <>
                  <button className="btn-secondary" onClick={() => setStatus('Ready')}><CheckCheck className="w-4 h-4" /> Ready</button>
                  <button className="btn-secondary" onClick={() => setStatus('Shipped')}><Ship className="w-4 h-4" /> Shipped</button>
                  <button className="btn-secondary" onClick={() => setStatus('Delivered')}><PackageCheck className="w-4 h-4" /> Delivered</button>
                  <button className="btn-secondary" onClick={() => setStatus('Closed')}><CircleCheckBig className="w-4 h-4" /> Close</button>
                </>
              )}
            </div>
          </div>

          {/* Voyage / Dates */}
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs muted">Voyage</div>
                <div className="text-sm">{manifest.voyage || '—'}</div>
              </div>
              <div>
                <div className="text-xs muted">Vessel</div>
                <div className="text-sm">{manifest.vessel || '—'}</div>
              </div>
              <div>
                <div className="text-xs muted">ETA</div>
                <div className="text-sm">{fmtDate(manifest.eta)}</div>
              </div>
              <div>
                <div className="text-xs muted">ETD</div>
                <div className="text-sm">{fmtDate(manifest.etd)}</div>
              </div>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="card p-3 bg-[var(--surface-1)]">
              <div className="text-xs muted">Gross</div>
              <div className="text-lg font-semibold tabular-nums">{fmtNum(totals.gross)}</div>
            </div>
            <div className="card p-3 bg-[var(--surface-1)]">
              <div className="text-xs muted">Tare</div>
              <div className="text-lg font-semibold tabular-nums">{fmtNum(totals.tare)}</div>
            </div>
            <div className="card p-3 bg-[var(--surface-1)]">
              <div className="text-xs muted">Net</div>
              <div className="text-lg font-semibold tabular-nums">{fmtNum(totals.net)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Lines Table */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <span>Load Lines</span>
          <div className="flex items-center gap-2">
            {lines.length > 0 && (
              <button className="btn-secondary" onClick={exportLinesCSV}><Download className="w-4 h-4" /> Export CSV</button>
            )}
            {lines.length === 0 && (
              <button className="btn" onClick={addSampleLines}>Add Samples</button>
            )}
          </div>
        </div>
        <div className="card-body">
          {rows.length === 0 ? (
            <EmptyState title="No lines found for this container">
              Once loads are posted with this container number, they will appear here.
            </EmptyState>
          ) : (
            <>
              <DataTable columns={cols} rows={rows} filename={`manifest_${sanitize(manifest.container)}_lines.csv`} storageKey={TBL_LINES_KEY} />
              <div className="table-shell mt-2">
                <div className="flex justify-end px-3 py-2 text-sm tabular-nums">
                  <div className="flex gap-8">
                    <div><span className="muted mr-2">Gross</span>{fmtNum(totals.gross)}</div>
                    <div><span className="muted mr-2">Tare</span>{fmtNum(totals.tare)}</div>
                    <div><span className="muted mr-2">Net</span>{fmtNum(totals.net)}</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={`Edit • ${editing?.container || ''}`}>
        {editing && (
          <div className="drawer-panel space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm">Container #</label>
                <input className="input" value={editing.container} onChange={e => setEditing({ ...editing, container: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Seal #</label>
                <input className="input" value={editing.seal || ''} onChange={e => setEditing({ ...editing, seal: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Voyage</label>
                <input className="input" value={editing.voyage || ''} onChange={e => setEditing({ ...editing, voyage: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Vessel</label>
                <input className="input" value={editing.vessel || ''} onChange={e => setEditing({ ...editing, vessel: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Port</label>
                <input className="input" value={editing.port || ''} onChange={e => setEditing({ ...editing, port: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Site</label>
                <input className="input" value={editing.siteName || ''} onChange={e => setEditing({ ...editing, siteName: e.target.value })} />
              </div>
              <div>
                <label className="text-sm">Mill</label>
                <input className="input" value={editing.mill || ''} onChange={e => setEditing({ ...editing, mill: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-3 sm:col-span-2">
                <div>
                  <label className="text-sm">Gross (lbs)</label>
                  <input type="number" className="input" value={editing.gross ?? 0} onChange={e => setEditing({ ...editing, gross: Number(e.target.value || 0) })} />
                </div>
                <div>
                  <label className="text-sm">Tare (lbs)</label>
                  <input type="number" className="input" value={editing.tare ?? 0} onChange={e => setEditing({ ...editing, tare: Number(e.target.value || 0) })} />
                </div>
                <div>
                  <label className="text-sm">Net (lbs)</label>
                  <input type="number" className="input" value={editing.net ?? 0} onChange={e => setEditing({ ...editing, net: Number(e.target.value || 0) })} />
                </div>
              </div>
              <div>
                <label className="text-sm">ETA</label>
                <input type="date" className="input" value={editing.eta ? editing.eta.slice(0,10) : ''} onChange={e => setEditing({ ...editing, eta: e.target.value ? new Date(e.target.value).toISOString() : '' })} />
              </div>
              <div>
                <label className="text-sm">ETD</label>
                <input type="date" className="input" value={editing.etd ? editing.etd.slice(0,10) : ''} onChange={e => setEditing({ ...editing, etd: e.target.value ? new Date(e.target.value).toISOString() : '' })} />
              </div>
              <label className="inline-flex items-center gap-2 text-sm sm:col-span-2">
                <input type="checkbox" checked={!!editing.fumigation} onChange={(e) => setEditing({ ...editing, fumigation: e.target.checked })} />
                Fumigation
              </label>
              <div className="sm:col-span-2">
                <label className="text-sm">Notes</label>
                <input className="input" value={editing.notes || ''} onChange={e => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>

            <div className="flex gap-2">
              <button className="btn-secondary" onClick={() => setDrawerOpen(false)}>Close</button>
              <div className="flex-1" />
              {canManage && (
                <>
                  <button className="btn-secondary" onClick={() => setEditing(e => e ? { ...e, status: 'Ready' } : e)}><CheckCheck className="w-4 h-4" /> Ready</button>
                  <button className="btn-secondary" onClick={() => setEditing(e => e ? { ...e, status: 'Shipped' } : e)}><Ship className="w-4 h-4" /> Shipped</button>
                  <button className="btn-secondary" onClick={() => setEditing(e => e ? { ...e, status: 'Delivered' } : e)}><PackageCheck className="w-4 h-4" /> Delivered</button>
                </>
              )}
              <button className="btn" onClick={saveEdit}><Save className="w-4 h-4" /> Save</button>
            </div>
          </div>
        )}
      </Drawer>
    </AppShell>
  )
}

/* ---------------- helpers ---------------- */
function toLine(x: any): LoadLine {
  return {
    id: x.id || uid(),
    ticket: x.ticket || x.manifestTicket || '',
    date: x.date || x.loadedAt || '',
    siteName: x.siteName || x.site || '',
    product: x.product || x.productType || '',
    truck: x.truck || x.truckId || '',
    gross: Number(x.gross || x.grossWeight || 0),
    tare: Number(x.tare || x.tareWeight || 0),
    net: Number(x.net ?? x.netWeight ?? (Number(x.gross || x.grossWeight || 0) - Number(x.tare || x.tareWeight || 0))),
    container: x.container || '',
    seal: x.seal || '',
  }
}
function normalize(list: any[]): Manifest[] {
  return (list || []).map((m: any) => ({
    id: m.id || uid(),
    container: String(m.container || ''),
    seal: m.seal || '',
    voyage: m.voyage || '',
    vessel: m.vessel || '',
    port: m.port || '',
    siteId: m.siteId || '',
    siteName: m.siteName || '',
    mill: m.mill || '',
    gross: Number(m.gross || 0),
    tare: Number(m.tare || 0),
    net: Number(m.net ?? (Number(m.gross || 0) - Number(m.tare || 0))),
    fumigation: !!m.fumigation,
    eta: m.eta || '',
    etd: m.etd || '',
    status: (['Draft','Ready','Shipped','Delivered','Closed'].includes(m.status) ? m.status : 'Draft') as Status,
    notes: m.notes || '',
    createdAt: m.createdAt || new Date().toISOString(),
    updatedAt: m.updatedAt || m.createdAt || new Date().toISOString(),
  }))
}
function upsertById(list: Manifest[], item: Manifest) {
  const idx = list.findIndex(x => x.id === item.id)
  if (idx === -1) return [item, ...list]
  const next = [...list]; next[idx] = item; return next
}
function toCSV(rows: (string[])[]) {
  return rows.map(r => r.map(cell => /[",\n]/.test(cell) ? `"${cell.replace(/"/g,'""')}"` : cell).join(',')).join('\n')
}
function sanitize(name: string) { return name.replace(/[^\w\-]+/g,'_') }
